/**
 * Rewards Hub — /customer/refer (Feb 2026 rebuild)
 *
 * Replaces the legacy ReferralPage with a dedicated 4-section hub:
 *
 *   1. Hero — Rewards Status Card (dual tier card + referrals bar)
 *   2. Points Ledger — paginated + filterable (source + date range)
 *   3. Referrals — invite link + QR + pending/qualified referral cards
 *   4. How to Earn — three educational cards (Orders / Points / Referrals)
 *
 * Theme-aware — reads useTheme().isModern and swaps presentation accordingly.
 * Code theme preserves the existing neon-terminal aesthetic; Modern uses
 * the .am-modern-scope token ladder (bone backgrounds, matte-gold rewards,
 * emerald CTAs, Inter type).
 *
 * All business logic hooks are preserved: /api/users/me/tier,
 * /api/users/me/referrals, /api/loyalty/balance, /api/loyalty/ledger.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Copy, QrCode, Share2, Sparkles, Package, Coins, Users,
  Check, Clock, ChevronLeft, ChevronRight, Filter,
} from 'lucide-react';
import { toast } from 'sonner';

import { Navbar } from '../../components/Navbar';
import { BackButton } from '../../components/BackButton';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { CustomerTierCard } from '../../components/CustomerTierCard';
import { TierShowcase } from '../../components/TierShowcase';
import { usePoints } from '../../hooks/usePoints';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const PAGE_SIZE = 25;

// Friendly labels per ledger source. Mirrors backend
// `customer_tier._POINTS_TIER_ELIGIBLE_SOURCES` + redeem/admin. Keep
// in sync with the Code-theme legacy map (ReferralPage lines 266-281).
const SOURCE_META = {
  earn:                  { label: 'Earned from order',      tone: 'positive' },
  redeem:                { label: 'Redeemed at checkout',   tone: 'negative' },
  referral_bonus:        { label: 'Referral bonus',         tone: 'positive' },
  referral_qualified:    { label: 'Referral milestone bonus', tone: 'positive' },
  qr_signup_bonus:       { label: 'Welcome bonus',          tone: 'positive' },
  staff_qr_signup:       { label: 'Welcome bonus',          tone: 'positive' },
  staff_qr_first_order:  { label: 'First-order bonus',      tone: 'positive' },
  admin_adjust:          { label: 'Account credit',         tone: 'neutral' },
  transfer_in:           { label: 'Points received',        tone: 'positive' },
  transfer_out:          { label: 'Points sent',            tone: 'negative' },
  cancellation_reversal: { label: 'Order cancelled — pts returned', tone: 'negative' },
  earn_reversal:         { label: 'Order cancelled — pts removed',  tone: 'negative' },
  install_bonus:         { label: 'Install bonus',          tone: 'positive' },
  install_bonus_retro:   { label: 'Early-supporter thank-you', tone: 'positive' },
  founders_thanks:       { label: 'BOOST from the team',    tone: 'positive' },
  birthday_bonus:        { label: 'Birthday bonus',         tone: 'positive' },
  review_approval:       { label: 'Review bonus',           tone: 'positive' },
  review_like:           { label: 'Review ❤ reward',        tone: 'positive' },
};

const SOURCE_FILTER_OPTIONS = [
  ['all', 'All sources'],
  ['earn', 'Earned from orders'],
  ['redeem', 'Redeemed'],
  ['referral_bonus', 'Referral bonus'],
  ['referral_qualified', 'Referral milestone'],
  ['admin_adjust', 'Account credit'],
  ['birthday_bonus', 'Birthday'],
  ['review_approval', 'Reviews'],
];

const DATE_FILTER_OPTIONS = [
  ['all', 'All time'],
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
  ['ytd', 'Year to date'],
];


function dateCutoffFor(key) {
  const now = Date.now();
  const d = new Date();
  switch (key) {
    case '7d':  return new Date(now - 7 * 86400_000).toISOString();
    case '30d': return new Date(now - 30 * 86400_000).toISOString();
    case '90d': return new Date(now - 90 * 86400_000).toISOString();
    case 'ytd': return new Date(Date.UTC(d.getUTCFullYear(), 0, 1)).toISOString();
    default:    return null;
  }
}

// Format point delta for display: +100 pts / -50 pts.
function fmtDelta(d) {
  const n = Number(d) || 0;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toLocaleString()} pts`;
}

export function RewardsHub() {
  const { user } = useAuth();
  const { isModern } = useTheme();
  const queryClient = useQueryClient();
  const { balance: pointsBalance } = usePoints();

  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Tier (includes new referral path from Feb 2026 3-path hybrid)
  const tierQuery = useQuery({
    queryKey: ['customer', 'tier'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/users/me/tier`, { withCredentials: true });
      return data;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  // Referrals (code + list of referees with pips + qualified flag)
  const referralsQuery = useQuery({
    queryKey: ['customer', 'referrals'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/referrals/me`, { withCredentials: true });
      return data;
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  // Ledger — full history, paginated client-side after fetch.
  // Backend endpoint caps at 500 so this is adequate for any realistic user.
  const ledgerQuery = useQuery({
    queryKey: ['customer', 'ledger'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/users/me/points/history`, {
        withCredentials: true,
        params: { limit: 500 },
      });
      return data.history || [];
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  // Apply filters + paginate.
  const filteredLedger = useMemo(() => {
    const rows = ledgerQuery.data || [];
    const dateCutoff = dateCutoffFor(dateFilter);
    return rows.filter((row) => {
      if (sourceFilter !== 'all' && row.source !== sourceFilter) return false;
      if (dateCutoff && row.created_at && row.created_at < dateCutoff) return false;
      return true;
    });
  }, [ledgerQuery.data, sourceFilter, dateFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredLedger.length / PAGE_SIZE));
  const pageRows = filteredLedger.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 whenever filters change.
  useEffect(() => { setPage(1); }, [sourceFilter, dateFilter]);

  const referralCode = referralsQuery.data?.code || '…';
  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied!');
    } catch {
      toast.error('Copy failed — long-press the link to copy manually');
    }
  };

  const share = async () => {
    const text = `Join me on Ash & Merch — use my code ${referralCode} and we both get bonus points. ${referralLink}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Ash & Merch', text, url: referralLink }); return; } catch { /* user cancelled */ }
    }
    await copyLink();
  };

  // ─── Theme-aware wrapper classes ────────────────────────────────────
  const wrapperClass = isModern ? 'am-modern-scope min-h-screen' : 'min-h-screen bg-[#050003] text-white';
  const containerStyle = isModern ? { background: 'var(--am-canvas)' } : {};

  return (
    <div className={wrapperClass} style={containerStyle} data-testid="rewards-hub">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-8">
        <BackButton to="/customer" />

        <div>
          <h1 className={isModern
            ? 'am-section-title text-3xl'
            : "text-[#39FF14] font-mono text-2xl tracking-wider"}
            data-testid="rewards-hub-title"
          >
            {isModern ? 'Rewards' : '> REWARDS_HUB'}
          </h1>
          <p className={isModern ? 'text-sm mt-1' : 'text-xs mt-1 font-mono text-[#A3C7A8]'}
             style={isModern ? { color: 'var(--am-ink-3)' } : {}}
          >
            {isModern
              ? 'Earn via orders, points, or referrals — whichever grows fastest for you.'
              : '// Three paths. Highest wins.'}
          </p>
        </div>

        {/* ─── 1. HERO — Tier card with 3-path progress ───────────── */}
        {tierQuery.data && (
          <CustomerTierCard
            tier={tierQuery.data.effective_tier || tierQuery.data.tier || 'standard'}
            annualOrders={Number(tierQuery.data.annual_orders) || 0}
            annualPoints={Number(tierQuery.data.annual_points) || 0}
            annualReferrals={Number(tierQuery.data.annual_referrals) || 0}
            toNextOrders={tierQuery.data.to_next_orders ?? null}
            toNextPoints={tierQuery.data.to_next_points ?? null}
            toNextReferrals={tierQuery.data.to_next_referrals ?? null}
            nextTier={tierQuery.data.next_tier ?? null}
            qualifyingPath={tierQuery.data.qualifying_path ?? null}
            pointsBalance={Number(pointsBalance) || 0}
            isLocked={Boolean(tierQuery.data.locked_until)}
            hideNextPerks
          />
        )}
        {tierQuery.data && (
          <TierShowcase currentTier={tierQuery.data.effective_tier || tierQuery.data.tier || 'standard'} />
        )}

        {/* ─── 3. REFERRALS SECTION ───────────────────────────────── */}
        <section data-testid="rewards-hub-referrals">
          <SectionHeader isModern={isModern} icon={Users} title="Your referrals"
            sub="Invite friends — qualify when they complete their 3rd order." />

          {/* Code + QR + Share */}
          <div className={isModern
            ? 'am-card p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'
            : 'border border-[#2A0F3D] bg-[#0C0612] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'
          }>
            <div>
              <div className={isModern
                ? 'text-xs uppercase tracking-wider mb-1'
                : 'text-[#A3C7A8] text-xs uppercase tracking-wider font-mono mb-1'}
                style={isModern ? { color: 'var(--am-ink-3)' } : {}}
              >
                Your code
              </div>
              <div className={isModern
                ? 'text-2xl font-bold tracking-wide'
                : 'text-[#39FF14] font-mono text-2xl tracking-widest'}
                data-testid="referral-code"
              >
                {referralCode}
              </div>
              <div className={isModern
                ? 'text-xs mt-2 break-all'
                : 'text-[#6B8E73] text-xs font-mono mt-2 break-all'}
                style={isModern ? { color: 'var(--am-ink-3)' } : {}}
                data-testid="referral-link"
              >
                {referralLink}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={copyLink}
                data-testid="referral-copy-btn"
                className={isModern ? 'am-cta' : 'bg-[#39FF14] text-black hover:bg-[#2BC910]'}
              >
                <Copy className="w-4 h-4 mr-2" /> Copy link
              </Button>
              <Button
                onClick={share}
                variant="outline"
                data-testid="referral-share-btn"
                className={isModern ? '' : 'border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10'}
              >
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
              <Button
                variant="outline"
                data-testid="referral-qr-btn"
                className={isModern ? '' : 'border-[#A3C7A8] text-[#A3C7A8] hover:bg-[#A3C7A8]/10'}
                onClick={() => {
                  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(referralLink)}`;
                  window.open(qrUrl, '_blank', 'noopener');
                }}
              >
                <QrCode className="w-4 h-4 mr-2" /> QR
              </Button>
            </div>
          </div>

          {/* Referee cards */}
          <div className="mt-4 space-y-3">
            {referralsQuery.data?.referrals?.length ? (
              referralsQuery.data.referrals.map((r) => (
                <ReferralCard
                  key={r.id}
                  r={r}
                  isModern={isModern}
                />
              ))
            ) : (
              <div className={isModern
                ? 'am-card p-6 text-center text-sm'
                : 'border border-[#2A0F3D] bg-[#0C0612] p-6 text-center text-xs font-mono text-[#6B8E73]'}
                style={isModern ? { color: 'var(--am-ink-3)' } : {}}
                data-testid="referrals-empty"
              >
                {isModern
                  ? 'No referrals yet — share your code to get started.'
                  : '// No referrals yet. Share your code to start.'}
              </div>
            )}
          </div>
        </section>

        {/* ─── 2. POINTS LEDGER with filters + pagination ─────────── */}
        <section data-testid="rewards-hub-ledger">
          <SectionHeader isModern={isModern} icon={Coins} title="Points activity"
            sub="Every point you've earned or spent." />

          {/* Filters */}
          <div className="flex gap-2 flex-wrap mb-3">
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger
                className={isModern ? 'w-44' : 'w-44 bg-[#0C0612] border-[#2A0F3D] text-[#39FF14]'}
                data-testid="ledger-source-filter"
              >
                <Filter className="w-3 h-3 mr-2 inline" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_FILTER_OPTIONS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger
                className={isModern ? 'w-36' : 'w-36 bg-[#0C0612] border-[#2A0F3D] text-[#39FF14]'}
                data-testid="ledger-date-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FILTER_OPTIONS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {pageRows.length > 0 ? (
            <>
              <ul className={isModern
                ? 'am-card divide-y'
                : 'border border-[#2A0F3D] bg-[#0C0612] divide-y divide-[#2A0F3D]'}
                style={isModern ? { borderColor: 'var(--am-hair)' } : {}}
                data-testid="ledger-list"
              >
                {pageRows.map((row) => {
                  const meta = SOURCE_META[row.source] || { label: row.source, tone: 'neutral' };
                  const delta = Number(row.delta) || 0;
                  const when = row.created_at ? new Date(row.created_at).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  }) : '';
                  return (
                    <li
                      key={row.id}
                      className="flex items-start justify-between gap-3 p-3"
                      data-testid={`ledger-row-${row.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className={isModern
                          ? 'text-xs uppercase tracking-wider font-semibold'
                          : 'text-[#39FF14] text-xs uppercase tracking-wider font-mono'}
                          style={isModern ? { color: 'var(--am-ink-2)' } : {}}
                        >
                          {meta.label}
                        </p>
                        <p className={isModern
                          ? 'text-sm mt-1 break-words'
                          : 'text-[#A3C7A8] text-xs mt-1 break-words'}
                          style={isModern ? { color: 'var(--am-ink-3)' } : {}}
                          title={row.reason}
                        >
                          {row.reason}
                        </p>
                        {when && (
                          <p className={isModern
                            ? 'text-[11px] mt-1'
                            : 'text-[#4B6B52] text-[10px] font-mono mt-1'}
                            style={isModern ? { color: 'var(--am-ink-3)' } : {}}
                          >
                            {when}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={`font-bold text-base ${isModern ? '' : 'font-mono'}`}
                           style={{ color: delta > 0 ? (isModern ? 'var(--am-emerald-600)' : '#39FF14') : (isModern ? '#DC2626' : '#FF9F0A') }}
                        >
                          {fmtDelta(delta)}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3" data-testid="ledger-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="ledger-prev"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </Button>
                  <span className={isModern ? 'text-xs' : 'text-xs font-mono text-[#A3C7A8]'}
                    style={isModern ? { color: 'var(--am-ink-3)' } : {}}
                  >
                    Page {page} / {totalPages} ({filteredLedger.length} total)
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    data-testid="ledger-next"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className={isModern
              ? 'am-card p-6 text-center text-sm'
              : 'border border-[#2A0F3D] bg-[#0C0612] p-6 text-center text-xs font-mono text-[#6B8E73]'}
              style={isModern ? { color: 'var(--am-ink-3)' } : {}}
              data-testid="ledger-empty"
            >
              {isModern
                ? 'No point activity matches your filters.'
                : '// No point activity matches your filters.'}
            </div>
          )}
        </section>

        {/* ─── 4. HOW TO EARN ─────────────────────────────────────── */}
        <section data-testid="rewards-hub-howto">
          <SectionHeader isModern={isModern} icon={Sparkles} title="How to earn tier status"
            sub="Three paths. Whichever path you complete first bumps your tier." />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <HowToCard
              isModern={isModern}
              icon={Package}
              title="Orders"
              body="3 → Silver · 10 → Gold · 25 → Platinum · 50 → Onyx. Every delivered order counts for the full year."
              accent={isModern ? 'var(--am-emerald-600)' : '#39FF14'}
            />
            <HowToCard
              isModern={isModern}
              icon={Coins}
              title="Points"
              body="5k → Silver · 15k → Gold · 40k → Platinum · 100k → Onyx. Earn via orders, reviews, birthday, and more."
              accent={isModern ? 'var(--am-gold-500, #D4AF37)' : '#FFD700'}
            />
            <HowToCard
              isModern={isModern}
              icon={Users}
              title="Referrals"
              body="5 → Gold · 10 → Platinum · 15 → Onyx. A referral qualifies when your friend completes their 3rd order."
              accent={isModern ? 'var(--am-gold-500, #D4AF37)' : '#FFD700'}
              isReferrals
            />
          </div>
        </section>
      </div>
    </div>
  );
}


// ───────────────────── sub-components ───────────────────────────────

function SectionHeader({ isModern, icon: Icon, title, sub }) {
  return (
    <div className="mb-4">
      <h2 className={isModern
        ? 'am-section-title text-xl flex items-center gap-2'
        : 'text-[#39FF14] font-mono text-sm uppercase tracking-wider flex items-center gap-2'}
      >
        <Icon className="w-5 h-5" />
        <span>{title}</span>
      </h2>
      {sub && (
        <p className={isModern
          ? 'text-sm mt-1'
          : 'text-[#A3C7A8] text-xs font-mono mt-1'}
          style={isModern ? { color: 'var(--am-ink-3)' } : {}}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function ReferralCard({ r, isModern }) {
  const qualified = r.qualified || r.pips >= 3;
  const inProgress = !qualified && r.pips < 3;

  // Pip colors — Code: neon green when done, cyan outline when pending.
  // Modern: emerald when done, matte-gold when pending (amber-ish).
  const pipDone = isModern ? 'var(--am-emerald-600)' : '#39FF14';
  const pipPending = isModern ? '#D4AF37' : 'rgba(163,199,168,0.3)';
  const borderActive = qualified
    ? (isModern ? 'var(--am-emerald-600)' : '#39FF14')
    : (isModern ? 'rgba(212,175,55,0.4)' : '#A3C7A8');

  return (
    <div
      className={isModern ? 'am-card p-4' : 'border bg-[#0C0612] p-4'}
      style={{
        borderColor: borderActive,
        transition: 'border-color .35s ease, box-shadow .35s ease',
        boxShadow: qualified
          ? (isModern ? '0 0 0 1px var(--am-emerald-600) inset' : `0 0 24px ${pipDone}22`)
          : 'none',
      }}
      data-testid={`referral-card-${r.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={isModern
              ? 'font-semibold text-sm truncate'
              : 'text-[#39FF14] font-mono text-sm truncate'}
              data-testid={`referral-card-name-${r.id}`}
            >
              {r.name || r.email_masked || 'Friend'}
            </p>
            {qualified && (
              <span className={isModern
                ? 'am-chip am-active text-[10px]'
                : 'text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-[#39FF14] text-[#39FF14]'}
                data-testid={`referral-card-qualified-${r.id}`}
              >
                <Check className="w-3 h-3 inline mr-1" />
                Qualified
              </span>
            )}
            {inProgress && (
              <span className={isModern
                ? 'text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full'
                : 'text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border'}
                style={{
                  color: isModern ? '#B8860B' : '#D4AF37',
                  background: isModern ? 'rgba(212,175,55,0.1)' : 'transparent',
                  borderColor: isModern ? 'rgba(212,175,55,0.4)' : '#D4AF37',
                }}
                data-testid={`referral-card-progress-${r.id}`}
              >
                <Clock className="w-3 h-3 inline mr-1" />
                {r.pips}/3 orders
              </span>
            )}
          </div>
          <p className={isModern ? 'text-xs mt-1' : 'text-[#A3C7A8] text-xs font-mono mt-1'}
             style={isModern ? { color: 'var(--am-ink-3)' } : {}}
          >
            {r.email_masked}
          </p>
        </div>

        {/* 3-pip tracker */}
        <div className="flex items-center gap-1.5 shrink-0" data-testid={`referral-card-pips-${r.id}`} aria-label={`${r.pips} of 3 orders`}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-full"
              style={{
                background: i < r.pips ? pipDone : pipPending,
                border: i >= r.pips ? `1px solid ${pipPending}` : 'none',
                transition: 'background-color .4s ease',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function HowToCard({ isModern, icon: Icon, title, body, accent, isReferrals }) {
  return (
    <div
      className={isModern ? 'am-card p-4' : 'border border-[#2A0F3D] bg-[#0C0612] p-4'}
      style={isReferrals && isModern ? { borderColor: 'rgba(212,175,55,0.4)' } : {}}
      data-testid={`howto-card-${title.toLowerCase()}`}
    >
      <Icon className="w-6 h-6 mb-2" style={{ color: accent }} />
      <h3 className={isModern
        ? 'font-semibold text-base mb-1'
        : 'text-sm font-mono uppercase tracking-wider mb-1'}
        style={{ color: accent }}
      >
        {title}
      </h3>
      <p className={isModern ? 'text-sm leading-relaxed' : 'text-xs font-mono leading-relaxed'}
         style={isModern ? { color: 'var(--am-ink-2)' } : { color: '#E8F3EA' }}
      >
        {body}
      </p>
    </div>
  );
}

export default RewardsHub;
